import React, { useRef, useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { OptimizedImage } from '@/components/OptimizedImage';
import { getBlurPlaceholder } from '@/utils/blurPlaceholders';
import { 
    Heart, 
    UtensilsCrossed, 
    Waves,
    Coffee,
    Wine,
    Eye,
    Activity,
    Thermometer,
    Sun,
    Trees,
    Mountain,
    Sparkles,
    MapPin
} from 'lucide-react';

// Helper function to properly encode image paths with spaces and special characters
// This ensures compatibility with production servers that are case-sensitive
const encodeImagePath = (path: string): string => {
    return path.split('/').map((segment, index) => 
        index === 0 ? segment : encodeURIComponent(segment)
    ).join('/');
};

const AmenitiesOverviewSection: React.FC = () => {
    const { t } = useLanguage();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollContainerRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeft(scrollContainerRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    // Flatten all amenities into a single array with images and icons
    const amenities = [
        // Wellness & Spa
        {
            id: 'clinique',
            icon: Heart,
            title: t('activities.amenitiesOverview.wellness.clinique.name'),
            description: t('activities.amenitiesOverview.wellness.clinique.description'),
            image: '/images/activity arcade/Relaxing Massage.jpg',
            badge: undefined,
        },
        {
            id: 'whirlpool',
            icon: Waves,
            title: t('activities.amenitiesOverview.wellness.whirlpool.name'),
            description: t('activities.amenitiesOverview.wellness.whirlpool.description'),
            image: '/images/activity arcade/Whirlpool-mobile.webp',
            imageSrcSet: '/images/activity arcade/Whirlpool-mobile.webp 400w, /images/activity arcade/Whirlpool-desktop.webp 800w',
            badge: undefined,
        },
        {
            id: 'sauna',
            icon: Thermometer,
            title: 'Sauna',
            description: 'Relaxing sauna experience with panoramic alpine views',
            image: '/images/activity arcade/Sauna-mobile.webp',
            imageSrcSet: '/images/activity arcade/Sauna-mobile.webp 400w, /images/activity arcade/Sauna-desktop.webp 800w',
            badge: undefined,
        },
        {
            id: 'relaxationTerrace',
            icon: Sun,
            title: 'Relaxation Terrace',
            description: 'Outdoor terrace for peaceful relaxation with stunning alpine views',
            image: '/images/activity arcade/Relaxation-Terrace-mobile.webp',
            imageSrcSet: '/images/activity arcade/Relaxation-Terrace-mobile.webp 400w, /images/activity arcade/Relaxation-Terrace-desktop.webp 800w',
            badge: undefined,
        },
        // Dining & Bars (moved after whirlpool)
        {
            id: 'gourmet',
            icon: UtensilsCrossed,
            title: 'Gourmet Meals',
            description: 'Fine dining with panoramic mountain views',
            image: '/images/activity arcade/Gourmet-meals-mobile.webp',
            imageSrcSet: '/images/activity arcade/Gourmet-meals-mobile.webp 400w, /images/activity arcade/Gourmet-meals-desktop.webp 800w',
            badge: undefined,
        },
        {
            id: 'quickBites',
            icon: Coffee,
            title: 'Quick Bites',
            description: 'Casual alpine bistro with local specialties',
            image: '/images/activity arcade/Quick-bites-mobile.webp',
            imageSrcSet: '/images/activity arcade/Quick-bites-mobile.webp 400w, /images/activity arcade/Quick-bites-desktop.webp 800w',
            badge: undefined,
        },
        {
            id: 'lounge',
            icon: Wine,
            title: t('activities.amenitiesOverview.dining.lounge.name'),
            description: t('activities.amenitiesOverview.dining.lounge.description'),
            image: '/images/activity arcade/bar and lounge.webp',
            badge: undefined,
        },
        // Scenic Views
        {
            id: 'panoramic',
            icon: Eye,
            title: t('activities.amenitiesOverview.views.panoramic.name'),
            description: t('activities.amenitiesOverview.views.panoramic.description'),
            image: '/images/activity arcade/views.webp',
            badge: undefined,
        },
        // Sports & Fitness
        {
            id: 'tennis',
            icon: Activity,
            title: t('activities.amenitiesOverview.sports.tennis.name'),
            description: t('activities.amenitiesOverview.sports.tennis.description'),
            image: '/images/activity arcade/Tennis-closeup-4-3-mobile.webp',
            imageSrcSet: '/images/activity arcade/Tennis-closeup-4-3-mobile.webp 400w, /images/activity arcade/Tennis-closeup-4-3-desktop.webp 800w',
            badge: "COMING SOON"
        },
    ];

    // Track active card based on scroll position
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const updateActiveIndex = () => {
            const scrollLeft = container.scrollLeft;
            const containerWidth = container.clientWidth;
            const viewportCenter = scrollLeft + containerWidth / 2;
            
            const cards = container.querySelectorAll('[data-amenity-card]');
            if (cards.length === 0) return;

            let closestIndex = 0;
            let closestDistance = Infinity;

            cards.forEach((card, index) => {
                const cardRect = card.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const cardLeft = cardRect.left - containerRect.left + scrollLeft;
                const cardWidth = cardRect.width;
                const cardCenter = cardLeft + cardWidth / 2;
                const distance = Math.abs(cardCenter - viewportCenter);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            const maxScroll = container.scrollWidth - container.clientWidth;
            if (scrollLeft <= 10) {
                setActiveIndex(0);
            } else if (scrollLeft >= maxScroll - 10) {
                setActiveIndex(amenities.length - 1);
            } else {
                setActiveIndex(closestIndex);
            }
        };

        container.addEventListener('scroll', updateActiveIndex);
        updateActiveIndex();

        window.addEventListener('resize', updateActiveIndex);

        return () => {
            container.removeEventListener('scroll', updateActiveIndex);
            window.removeEventListener('resize', updateActiveIndex);
        };
    }, [amenities.length]);

    return (
        <section className="bg-pebble-beige py-20 md:py-24 relative overflow-visible">
            {/* Smooth gradient transition from elegant-white to pebble-beige at top */}
            <div className="absolute top-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-b from-elegant-white to-pebble-beige pointer-events-none z-0" />
            {/* Section Header - Constrained width */}
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 mb-12 md:mb-16 relative z-10">
                <div className="text-center">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-playfair text-charcoal-gray mb-4 leading-tight">
                        {t('activities.amenitiesOverview.title')}
                    </h2>
                    <p className="text-lg md:text-xl text-charcoal-gray/80 font-lato leading-relaxed max-w-3xl mx-auto">
                        {t('activities.amenitiesOverview.subtitle')}
                    </p>
                </div>
            </div>

            {/* Horizontal Scrolling Cards Container - Full width */}
            <div className="w-full overflow-visible">
                <div
                    ref={scrollContainerRef}
                    className={`flex gap-6 overflow-x-auto overflow-y-visible pt-12 pb-8 scrollbar-hide px-4 sm:px-6 lg:px-8 select-none relative z-20 ${
                        isDragging ? 'cursor-grabbing' : 'cursor-grab'
                    }`}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                >
                        {amenities.map((amenity, index) => {
                            const IconComponent = amenity.icon;
                            return (
                                <div
                                    key={amenity.id}
                                    className="group cursor-pointer flex-shrink-0 relative z-50 mt-8 mb-2"
                                    data-amenity-card
                                >
                                    {/* Card Container */}
                                    <div className="relative w-80 h-96 rounded-2xl overflow-hidden transition-transform duration-300 group-hover:-translate-y-14 z-50">
                                        {/* Optimized Image with srcset and performance hints */}
                                        <OptimizedImage
                                            src={encodeImagePath(amenity.image)}
                                            srcSet={amenity.imageSrcSet ? amenity.imageSrcSet : undefined}
                                            sizes="(max-width: 768px) 400px, 800px"
                                            alt={amenity.title}
                                            loading="lazy"
                                            decoding="async"
                                            width={800}
                                            height={960}
                                            className="absolute inset-0 w-full h-full object-cover"
                                            blurDataURL={getBlurPlaceholder(amenity.image)}
                                            fetchPriority="auto"
                                        />
                                        {/* Gradient Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-b from-pine-green/60 via-pine-green/10 to-transparent" />

                                        {/* Badge */}
                                        {amenity.badge && (
                                            <div className="absolute top-4 right-4 z-20">
                                                <div className="bg-alpine-gold text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-md">
                                                    {amenity.badge}
                                                </div>
                                            </div>
                                        )}

                                        {/* Title */}
                                        <div className="absolute top-0 left-0 right-0 p-6">
                                            <h3 className="font-playfair font-semibold text-elegant-white text-lg leading-tight">
                                                {amenity.title}
                                            </h3>
                                        </div>

                                        {/* Icon */}
                                        <div className="absolute bottom-4 right-4 w-10 h-10 bg-elegant-white/40 rounded-full flex items-center justify-center backdrop-blur-sm">
                                            <IconComponent
                                                className="text-gray-900"
                                                size={20}
                                            />
                                        </div>
                                    </div>

                                    {/* Description Card */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-elegant-white rounded-b-2xl shadow-lg px-6 pt-6 pb-3 transform translate-y-0 opacity-100 transition-all duration-300 z-40">
                                        <p className="font-lato text-gray-900 text-sm leading-relaxed">
                                            {amenity.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                {/* Scroll Indicators */}
                <div className="flex justify-center items-center mt-2.5 space-x-2">
                    <div className="flex space-x-2">
                        {amenities.map((_, index) => (
                            <div
                                key={index}
                                className={`rounded-full transition-all duration-300 ${
                                    index === activeIndex
                                        ? 'w-2.5 h-2.5 bg-charcoal-gray'
                                        : 'w-2 h-2 bg-charcoal-gray/30'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AmenitiesOverviewSection;
